import { GLYPH_KEYS, type GlyphKey } from '@kk/domain';
import { Icon, Text } from '@kk/ui';
import { useMemo, useState } from 'react';

import { deck } from '@/copy/deck';

/**
 * **PICK A PICTURE; YOU CANNOT TYPE ONE.**
 *
 * Evan, 2026-09-21: *"an author picks a glyph key from @kk/ui rather than typing one, and an unknown key is
 * refused outright rather than falling back the way a child's screen does. A picture silently standing in for
 * the one they meant is an error only the author could catch."*
 *
 * **Every option here is a drawing this build actually ships** — the list is `GLYPH_KEYS`, and `@kk/ui`'s
 * `glyphs` is declared `satisfies Record<GlyphKey, Glyph>`, so a key with no drawing is a compile error rather
 * than a blank square at the bottom of this grid. The author sees the picture, not the name, because the name
 * is not the thing they are choosing.
 *
 * **There is no free-text escape and no "other".** The API refuses an unknown key, so a box would only ever
 * produce an error the author had to decode; the affordance and the rule agree.
 *
 * **"No picture" is a real choice and says what it does.** A tile without a glyph draws its own caption as
 * type, which is right for the 95 of 99 live tile ids that are answers rather than things — "Borrow",
 * "59.50", "Rich people". Calling it "None" would read as a mistake; it is the common case.
 */
export function GlyphPicker({
  value,
  onChange,
  testID,
}: {
  /** The chosen key, or undefined for "no picture". */
  value: string | undefined;
  onChange: (key: GlyphKey | undefined) => void;
  testID?: string;
}) {
  const [query, setQuery] = useState('');
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? GLYPH_KEYS.filter((k) => k.includes(q)) : GLYPH_KEYS;
  }, [query]);

  return (
    <div className="glyph-picker" data-testid={testID}>
      <input
        className="glyph-search"
        type="search"
        value={query}
        placeholder={deck.o06.searchGlyphs}
        aria-label={deck.o06.searchGlyphs}
        onChange={(e) => setQuery(e.target.value)}
        data-testid={testID ? `${testID}-search` : undefined}
      />
      <div className="glyph-grid" role="listbox" aria-label={deck.o06.pickPicture}>
        {/*
         * The empty choice sits FIRST and is worded for what it does, not for what it lacks. A tile that draws
         * its answer as type is the ordinary case in this curriculum, not a failure to choose.
         */}
        <button
          type="button"
          role="option"
          aria-selected={value === undefined}
          className={`glyph-option glyph-none${value === undefined ? ' is-chosen' : ''}`}
          onClick={() => onChange(undefined)}
          data-testid={testID ? `${testID}-none` : undefined}
        >
          <span className="glyph-none-mark" aria-hidden>
            Aa
          </span>
          <Text variant="bodySm" tone="muted">
            {deck.o06.noPicture}
          </Text>
        </button>
        {matches.map((key) => (
          <button
            key={key}
            type="button"
            role="option"
            aria-selected={value === key}
            // the NAME is the accessible label; the picture is what the author reads
            aria-label={key}
            title={key}
            className={`glyph-option${value === key ? ' is-chosen' : ''}`}
            onClick={() => onChange(key)}
            data-testid={testID ? `${testID}-${key}` : undefined}
          >
            <Icon name={key} size={28} />
            <span className="glyph-key">{key}</span>
          </button>
        ))}
      </div>
      <Text variant="bodySm" tone="muted">
        {deck.o06.pickerHelper}
      </Text>
    </div>
  );
}
